<template>
    <div class="page-container">
        <div class="flex items-center justify-between mb-8">
            <div class="flex items-center gap-4">
                <div class="bg-purple-600 p-3 rounded-2xl shadow-lg">
                    <LucideIcon :size="24" class="text-white">
                        <path d="m15 18-3 3-3-3"/><path d="M12 21V3"/><path d="m9 6 3-3 3 3"/>
                    </LucideIcon>
                </div>
                <div>
                    <h2 class="page-title text-2xl font-bold text-slate-900">{{ $t('household.movementReport.title') }}</h2>
                    <p class="section-subtitle text-slate-500">{{ $t('household.movementReport.subtitle') }}</p>
                </div>
            </div>
        </div>

        <!-- Карта выгрузки -->
        <div class="export-card mb-8">
            <div class="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                <h3 class="font-extrabold text-slate-900 text-base uppercase tracking-tight">{{ $t('household.shared.makeExport') }}</h3>
            </div>
            <div class="p-6">
                <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div class="export-field">
                        <label class="export-label">{{ $t('household.movementReport.opTypeLabel') }}</label>
                        <select v-model="exportOpType" class="export-select">
                            <option value="">{{ $t('household.shared.allOps') }}</option>
                            <option value="receipt">{{ $t('household.operationTypes.receipt') }}</option>
                            <option value="consumption">{{ $t('household.operationTypes.consumption') }}</option>
                            <option value="movement">{{ $t('household.operationTypes.movement') }}</option>
                            <option value="writeoff">{{ $t('household.operationTypes.writeoff') }}</option>
                            <option value="inventory">{{ $t('household.operationTypes.inventory') }}</option>
                        </select>
                    </div>
                    <div class="export-field">
                        <label class="export-label">{{ $t('household.movementReport.warehouseLabel') }}</label>
                        <select v-model="exportWarehouse" class="export-select">
                            <option value="">{{ $t('household.shared.allWarehouses') }}</option>
                            <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.name }}</option>
                        </select>
                    </div>
                    <div class="export-field">
                        <label class="export-label">{{ $t('household.shared.dateFrom') }}</label>
                        <DatePicker v-model="exportDateFrom" format="iso" class="export-input" />
                    </div>
                    <div class="export-field">
                        <label class="export-label">{{ $t('household.shared.dateTo') }}</label>
                        <DatePicker v-model="exportDateTo" format="iso" class="export-input" />
                    </div>
                </div>
                <button
                    @click="handleExport"
                    class="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-all shadow-md active:scale-95"
                >
                    <LucideIcon :size="18"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></LucideIcon>
                    {{ $t('household.shared.exportBtn') }}
                </button>
            </div>
        </div>

        <!-- Итоги -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div class="stat-card">
                <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.movementReport.totalOps') }}</p>
                <p class="text-4xl font-extrabold text-slate-900">{{ filteredOperations.length }}</p>
            </div>
            <div class="stat-card">
                <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.movementReport.totalPositions') }}</p>
                <p class="text-4xl font-extrabold text-slate-900">{{ movements.length }}</p>
            </div>
            <div class="stat-card">
                <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.movementReport.totalSum') }}</p>
                <p class="text-4xl font-extrabold text-slate-900">{{ totalCost.toLocaleString() }} ₸</p>
            </div>
        </div>

        <!-- Фильтры -->
        <div class="filter-card mb-8">
            <div class="flex items-center gap-2 mb-4">
                <LucideIcon :size="18" class="text-slate-400"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></LucideIcon>
                <span class="text-sm font-bold text-slate-500 uppercase tracking-wider">{{ $t('household.movementReport.parameters') }}</span>
            </div>
            <div class="flex flex-wrap gap-6">
                <div>
                    <label class="filter-label">{{ $t('household.movementReport.periodFrom') }}</label>
                    <DatePicker
                        v-model="startDate"
                        format="iso"
                        class="search-input"
                    />
                </div>
                <div>
                    <label class="filter-label">{{ $t('household.movementReport.periodTo') }}</label>
                    <DatePicker
                        v-model="endDate"
                        format="iso"
                        class="search-input"
                    />
                </div>
                <div>
                    <label class="filter-label">{{ $t('household.movementReport.opTypeLabel') }}</label>
                    <select
                        v-model="filterType"
                        class="type-select h-[42px] px-3"
                    >
                        <option value="all">{{ $t('household.shared.allOps') }}</option>
                        <option value="receipt">{{ $t('household.operationTypes.receipt') }}</option>
                        <option value="consumption">{{ $t('household.operationTypes.consumption') }}</option>
                        <option value="movement">{{ $t('household.operationTypes.movement') }}</option>
                        <option value="writeoff">{{ $t('household.operationTypes.writeoff') }}</option>
                        <option value="inventory">{{ $t('household.operationTypes.inventory') }}</option>
                    </select>
                </div>
                <div>
                    <label class="filter-label">{{ $t('household.movementReport.warehouseLabel') }}</label>
                    <select
                        v-model="filterWarehouse"
                        class="type-select h-[42px] px-3"
                    >
                        <option value="all">{{ $t('household.shared.allWarehouses') }}</option>
                        <option v-for="w in warehouses" :key="w.id" :value="w.id">
                            {{ w.name }}
                        </option>
                    </select>
                </div>
            </div>
        </div>

        <!-- Таблица -->
        <div class="table-container">
            <div v-if="loading" class="flex justify-center py-20">
                <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
            </div>
            <div v-else class="overflow-x-auto">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>{{ $t('household.movementReport.colDate') }}</th>
                            <th>{{ $t('household.movementReport.colOperation') }}</th>
                            <th class="w-1/4">{{ $t('household.movementReport.colNomenclature') }}</th>
                            <th class="text-right">{{ $t('household.movementReport.colQuantity') }}</th>
                            <th class="text-right">{{ $t('household.movementReport.colSum') }}</th>
                            <th>{{ $t('household.movementReport.colWarehouse') }}</th>
                            <th>{{ $t('household.movementReport.colCounterparty') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(movement, index) in movements" :key="index">
                            <td class="font-bold text-slate-600">
                                {{ formatDate(movement.date) }}
                            </td>
                            <td>
                                <span :class="['px-3 py-1 text-[10px] rounded-lg font-extrabold uppercase tracking-wider border', getOperationBadgeClass(movement.operationType)]">
                                    {{ $t('household.operationTypes.' + movement.operationType) }}
                                </span>
                            </td>
                            <td>
                                <div class="font-extrabold text-slate-900 text-sm mb-0.5">{{ movement.nomenclature?.name }}</div>
                                <div v-if="movement.notes" class="text-xs text-slate-400 italic">{{ movement.notes }}</div>
                            </td>
                            <td class="text-right font-extrabold text-slate-900">
                                {{ movement.quantity.toLocaleString() }} <span class="text-[10px] text-slate-400 font-bold uppercase ml-1">{{ movement.nomenclature?.unit }}</span>
                            </td>
                            <td class="text-right font-extrabold text-slate-900">
                                {{ movement.cost.toLocaleString() }} ₸
                            </td>
                            <td class="text-slate-600 font-bold">
                                {{ movement.warehouse }}
                            </td>
                            <td class="text-slate-500 font-medium">
                                {{ movement.counterparty || '—' }}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div v-if="movements.length === 0" class="text-center py-20 text-slate-500 font-medium">
                    {{ $t('household.shared.noData') }}
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.page-container { padding: 2rem; background: #f3f3f5; min-height: 100%; }
.page-title { color: #000000; font-size: 1.75rem; font-weight: 700; margin: 0; }
.section-subtitle { color: #64748b; font-size: 0.875rem; margin-top: 0.25rem; }

.stat-card {
    background: white;
    padding: 1.5rem 2rem;
    border-radius: 1.25rem;
    border: 1px solid #eef0f4;
    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01);
}

.filter-card {
    background: white;
    padding: 1.75rem;
    border-radius: 1.25rem;
    border: 1px solid #eef0f4;
    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
}
.filter-label { display: block; font-size: 0.75rem; font-weight: 700; color: #64748b; text-transform: uppercase; margin-bottom: 0.5rem; letter-spacing: 0.025em; }

.search-input, .type-select {
    background: #ffffff;
    border: 1px solid #eef0f4;
    border-radius: 1rem;
    color: #030229;
    font-size: 0.875rem;
    font-weight: 600;
    outline: none;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.search-input:focus, .type-select:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.08);
}

.table-container {
    background: white;
    border-radius: 1.25rem;
    border: 1px solid #e2e8f0;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.data-table { width: 100%; border-collapse: collapse; }
.data-table th {
    background: #ffffff;
    text-align: left;
    padding: 1.25rem 1.5rem;
    color: #94a3b8;
    font-size: 0.65rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    border-bottom: 1px solid #f1f5f9;
}
.data-table td { padding: 1.5rem; border-top: 1px solid #f8fafc; }
.data-table tr:hover { background: #fbfcfe; }

.export-card {
    background: white;
    border-radius: 1.25rem;
    border: 1px solid #eef0f4;
    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
}
.export-field { display: flex; flex-direction: column; }
.export-label { font-size: 0.7rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.4rem; }
.export-select, .export-input {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
    padding: 0.6rem 0.75rem;
    font-size: 0.85rem;
    color: #1e293b;
    outline: none;
    transition: all 0.2s;
}
.export-select:focus, .export-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }
</style>

<script setup>
import { ref, onMounted, computed } from 'vue';
import LucideIcon from '@/components/common/LucideIcon.vue';
import { getMovementReport } from '@/api/household_reports';
import { getNomenclatures } from '@/api/household_nomenclatures';
import { getWarehouses } from '@/api/household_warehouses';
import { getCounterparties } from '@/api/household_counterparties';
import DatePicker from '@/components/widgets/DatePicker.vue';

const loading = ref(true);
const operations = ref([]);
const nomenclatures = ref([]);
const warehouses = ref([]);
const counterparties = ref([]);

const startDate = ref(new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0]);
const endDate = ref(new Date().toISOString().split('T')[0]);
const filterType = ref('all');
const filterWarehouse = ref('all');

const exportOpType = ref('');
const exportWarehouse = ref('');
const exportDateFrom = ref('');
const exportDateTo = ref('');

const getOperationBadgeClass = (type) => {
    switch (type) {
        case 'receipt': return 'bg-green-100 text-green-800';
        case 'consumption': return 'bg-blue-100 text-blue-800';
        case 'movement': return 'bg-purple-100 text-purple-800';
        case 'writeoff': return 'bg-red-100 text-red-800';
        default: return 'bg-gray-100 text-gray-800';
    }
};

const filteredOperations = computed(() => {
    return operations.value.filter(op => {
        const opDate = new Date(op.date);
        const start = new Date(startDate.value);
        const end = new Date(endDate.value);
        
        const matchesDate = opDate >= start && opDate <= end;
        const matchesType = filterType.value === 'all' || op.type === filterType.value;
        const matchesWarehouse = filterWarehouse.value === 'all' || op.warehouseId === filterWarehouse.value;
        
        return matchesDate && matchesType && matchesWarehouse;
    });
});

const movements = computed(() => {
    return filteredOperations.value.flatMap(op => {
        const warehouse = warehouses.value.find(w => w.id === op.warehouseId);
        const counterparty = op.counterpartyId 
            ? counterparties.value.find(c => c.id === op.counterpartyId)
            : null;

        return op.items.map(item => {
            const nomenclature = nomenclatures.value.find(n => n.id === item.nomenclatureId);
            return {
                date: op.date,
                operationType: op.type,
                nomenclature,
                quantity: item.quantity,
                cost: item.cost,
                warehouse: warehouse?.name,
                counterparty: counterparty?.name,
                notes: op.notes,
            };
        });
    });
});

const totalCost = computed(() => movements.value.reduce((sum, m) => sum + m.cost, 0));

const fetchData = async () => {
    loading.value = true;
    try {
        const [opRes, nomRes, whRes, cpRes] = await Promise.all([
            getMovementReport(),
            getNomenclatures(),
            getWarehouses(),
            getCounterparties()
        ]);
        operations.value = (Array.isArray(opRes.data) ? opRes.data : (opRes.data?.results || [])).map(op => ({
            ...op,
            items: op.nomenclatures || op.items || []
        }));
        nomenclatures.value = Array.isArray(nomRes.data) ? nomRes.data : (nomRes.data?.results || []);
        warehouses.value = Array.isArray(whRes.data) ? whRes.data : (whRes.data?.results || []);
        counterparties.value = Array.isArray(cpRes.data) ? cpRes.data : (cpRes.data?.results || []);
    } catch (error) {
        console.error('Error fetching movement report data:', error);
    } finally {
        loading.value = false;
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('ru-RU');
};

const handleExport = () => {
    const params = {
        operation_type: exportOpType.value || null,
        warehouse: exportWarehouse.value || null,
        date_from: exportDateFrom.value || null,
        date_to: exportDateTo.value || null
    };
    console.log('Экспорт движения с параметрами:', params);
};

onMounted(fetchData);
</script>
